/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { Confirm } from "notiflix/build/notiflix-confirm-aio";
import Box from "@mui/material/Box";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import MDBox from "components/MDBox";
import Divider from "@mui/material/Divider";
import DataTable from "examples/Tables/DataTable";
import { Loading } from "notiflix/build/notiflix-loading-aio";
import { globalCountry } from "store/entities/countries";
import {
  useLazyGetQuoteProductsByQuoteIdQuery,
  useLazyGetQuoteDetailsByQuoteIdQuery,
  useResendQuoteCreatedEmailMutation,
  useConvertQuoteMutation,
} from "services/quotes";
import { formatDate } from "utils/helperFunctions";
import { tables } from "utils/constants";
import DetailBlock from "components/DetailBlock";
import Alert from "components/CustomAlert";
import ConvertToOrderDialog from "./ConvertToOrderDialog";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const OrderDetails = ({ order, id }) => {
  const selectedCountry = useSelector(globalCountry);
  const [openOrderDetails, setOpenOrderDetails] = useState(false);
  const [fetchedQuotationDetails, setFetchedQuotationDetails] = useState({});
  const [isOrderProductsPhysical, setIsOrderProductsPhysical] = useState(false);
  const [openConvertDialog, setOpenConvertDialog] = useState(false);
  const [
    triggerProductsByOrderId,
    { data: orderProductByOrderId = [], isLoading: isLoadingProductsByOrderId },
  ] = useLazyGetQuoteProductsByQuoteIdQuery();

  const [triggerOrderDetails, { data: singleOrderDetails = [], isLoading: isOrderDetailsLoading }] =
    useLazyGetQuoteDetailsByQuoteIdQuery({
      // pollingInterval: 5000,
    });

  const [convertQuoteMutation] = useConvertQuoteMutation();
  const [resendQuoteCreatedEmail] = useResendQuoteCreatedEmailMutation();

  const handleClickOpen = () => {
    isLoadingProductsByOrderId &&
      isOrderDetailsLoading &&
      Loading.dots({
        svgColor: "rgba(159, 3, 3, 0.8)",
      });
    setOpenOrderDetails(true);
    triggerProductsByOrderId(order.id);
    triggerOrderDetails(order.id);
  };

  const handleClose = () => {
    setOpenOrderDetails(false);
  };

  useEffect(() => {
    !isLoadingProductsByOrderId && !isOrderDetailsLoading && Loading.remove(500);
  }, [isLoadingProductsByOrderId, isOrderDetailsLoading]);

  useEffect(() => {
    if (singleOrderDetails && singleOrderDetails.id) setFetchedQuotationDetails(singleOrderDetails);
  }, [singleOrderDetails]);

  const orderProductDetails = useMemo(() => {
    return orderProductByOrderId;
  }, [orderProductByOrderId]);

  useEffect(() => {
    if (orderProductByOrderId && orderProductByOrderId.length > 0) {
      let isPhysicalProductExist = false;
      for (let product of orderProductByOrderId) {
        if (product.is_digital_good == 0) isPhysicalProductExist = true;
      }
      setIsOrderProductsPhysical(isPhysicalProductExist);
    }
  }, [orderProductByOrderId]);

  const handleConvertToOrder = (data) => {
    data.id = fetchedQuotationDetails.id;
    data.countryId = selectedCountry.id;
    data.companyId = fetchedQuotationDetails.reseller.companies.id;
    data.resellerId = fetchedQuotationDetails.reseller_id;
    try {
      convertQuoteMutation(data)
        .then((response) => {
          if (response.data?.status === "success") {
            Notify.success("Quote converted to order!");
          } else {
            Notify.failure("Something went wrong!");
          }
        })
        .catch((e) => console.log(e));
    } catch (e) {
      console.log(e);
    } finally {
      setOpenConvertDialog(false);
    }
  };

  return (
    <>
      <Tooltip title="View Order" placement="top">
        <IconButton aria-label="view" size="small" id={`orderId${id}`} onClick={handleClickOpen}>
          <VisibilityIcon color="info" fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <BootstrapDialog
        aria-labelledby="customized-dialog-title"
        maxWidth="lg"
        sx={{
          "& .MuiPaper-root": {
            width: "1000px",
          },
        }}
        open={openOrderDetails}
      >
        <DialogTitle color="info" sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          Quote Details
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <MDBox sx={{ textAlign: "right" }}>
            {fetchedQuotationDetails.quote_status == "SENT" ? (
              <MDButton
                size="small"
                color="info"
                variant="gradient"
                sx={{ ml: 1, color: "#fff", fontSize: "10px!important", fontWeight: "400" }}
                onClick={() => setOpenConvertDialog(true)}
              >
                Convert to Order (Invoice)
              </MDButton>
            ) : (
              fetchedQuotationDetails.quote_status == "CONVERTED" && (
                <MDBadge
                  badgeContent="Converted to Sales Order"
                  color="success"
                  variant="gradient"
                  size="sm"
                />
              )
            )}
          </MDBox>
          <MDBox sx={{ mt: 2 }}>
            <GridContainer>
              {fetchedQuotationDetails.id && (
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <DetailBlock
                    blockTitle="SO Number"
                    blockDesc={`#${fetchedQuotationDetails.so_number}`}
                  />
                </GridItem>
              )}
              {fetchedQuotationDetails.datetime ? (
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <DetailBlock
                    blockTitle="Created Date"
                    blockDesc={formatDate(fetchedQuotationDetails.datetime)}
                  />
                </GridItem>
              ) : (
                ""
              )}
              {fetchedQuotationDetails.reseller_id && (
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <DetailBlock
                    blockTitle="Reseller ID"
                    blockDesc={`#${fetchedQuotationDetails.reseller_id}`}
                  />
                </GridItem>
              )}
              {fetchedQuotationDetails.contact_name && (
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <DetailBlock
                    blockTitle="Reseller Name"
                    blockDesc={fetchedQuotationDetails.contact_name}
                  />
                </GridItem>
              )}
              {order.reseller?.companies?.name && (
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <DetailBlock
                    blockTitle="Company Name"
                    blockDesc={order.reseller?.companies?.name}
                  />
                </GridItem>
              )}

              {fetchedQuotationDetails.contact_phone && (
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <DetailBlock
                    blockTitle="Reseller Contact"
                    blockDesc={fetchedQuotationDetails.contact_phone}
                  />
                </GridItem>
              )}
              {fetchedQuotationDetails.payment_method ? (
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <DetailBlock
                    blockTitle="Payment Method"
                    blockDesc={fetchedQuotationDetails.payment_method}
                  />
                </GridItem>
              ) : (
                ""
              )}

              {isOrderProductsPhysical ? (
                fetchedQuotationDetails.delivery_fee == 0 ? (
                  <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                    <DetailBlock
                      blockTitle="Shipping status"
                      blockDesc={
                        <MDBadge
                          badgeContent="Free shipping"
                          color="info"
                          variant="gradient"
                          size="sm"
                        />
                      }
                    />
                  </GridItem>
                ) : (
                  <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                    <DetailBlock
                      blockTitle="Shipping status"
                      blockDesc={
                        <MDBadge
                          badgeContent="Paid shipping"
                          color="success"
                          variant="gradient"
                          size="sm"
                        />
                      }
                    />
                  </GridItem>
                )
              ) : (
                ""
              )}
              {fetchedQuotationDetails.is_express_shipping ? (
                <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                  <DetailBlock
                    blockTitle="Express shipping status"
                    blockDesc={
                      <MDBadge
                        badgeContent="Express shipping"
                        color="info"
                        variant="gradient"
                        size="sm"
                      />
                    }
                  />
                </GridItem>
              ) : (
                ""
              )}
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <Divider />
              </GridItem>
            </GridContainer>
          </MDBox>
          {fetchedQuotationDetails && (
            <MDBox>
              <Box display="flex" alignItems="center" gap={1}>
                <MDTypography component="h6" color="info">
                  Quote Items
                </MDTypography>
                {fetchedQuotationDetails.has_custom_products ? (
                  <MDBadge badgeContent="Custom" color="info" variant="gradient" size="sm" />
                ) : (
                  <></>
                )}
              </Box>
              {orderProductDetails.length > 0 ? (
                <MDBox
                  sx={{
                    marginBottom: "3px",
                    "& table tr td": {
                      fontSize: "12px",
                      paddingLeft: "5px",
                      paddingRight: "5px",
                      fontWeight: 500,
                    },
                    "& table tr th": {
                      fontSize: "12px",
                      paddingLeft: "5px",
                      paddingRight: "5px",
                      color: "#121212",
                    },
                    "& table tr th .MuiBox-root": {
                      color: "#3f3f3f",
                    },
                    "& table tr td .MuiBox-root": {
                      color: "#3f3f3f",
                    },
                  }}
                >
                  <DataTable
                    id={tables.ORDER_PRODUCTS}
                    columns={[
                      {
                        Header: "Product Name",
                        accessor: "name",
                        Cell: ({ row: { original } }) =>
                          original.attributes != "" ? (
                            <MDBox>
                              <MDBox sx={{ fontSize: "13px" }}>{original.name}</MDBox>
                              <MDBox sx={{ fontWeight: 400, fontStyle: "italic" }}>
                                {original.attributes}{" "}
                                {original.discount != 0 ? (
                                  <MDBox component="span" sx={{ color: "#1A73E8 !important" }}>
                                    {" - " +
                                      selectedCountry.currency +
                                      " " +
                                      original.discount +
                                      " discounted"}
                                  </MDBox>
                                ) : (
                                  ""
                                )}
                              </MDBox>
                            </MDBox>
                          ) : (
                            <>
                              <MDBox sx={{ fontSize: "13px" }}>{original.name}</MDBox>
                              {original.attributes}{" "}
                              {original.discount != 0 ? (
                                <MDBox
                                  sx={{
                                    fontWeight: 400,
                                    fontStyle: "italic",
                                    color: "#1A73E8 !important",
                                  }}
                                >
                                  {selectedCountry.currency +
                                    " " +
                                    original.discount +
                                    " discounted"}
                                </MDBox>
                              ) : (
                                ""
                              )}
                            </>
                          ),
                      },
                      {
                        Header: "Quantity",
                        accessor: "quantity",
                        align: "center",
                      },
                      {
                        Header: "Price",
                        accessor: "price",
                        align: "center",
                        Cell: ({ row: { original } }) => (
                          <>
                            {selectedCountry.currency} {original.price?.toFixed(2)}
                          </>
                        ),
                      },
                    ]}
                    data={orderProductDetails || []}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    hideColumnSelection={true}
                    noEndBorder
                    canSearch={false}
                    unlimitedRows={true}
                  />
                </MDBox>
              ) : (
                <Alert severity="warning">No Quote products found!</Alert>
              )}
              <MDBox sx={{ mt: 2 }}>
                <GridContainer>
                  {fetchedQuotationDetails.subtotal ? (
                    <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                      <DetailBlock
                        blockTitle="Subtotal"
                        blockDesc={`${
                          selectedCountry.currency
                        } ${fetchedQuotationDetails.subtotal?.toFixed(2)}`}
                      />
                    </GridItem>
                  ) : (
                    ""
                  )}
                  {fetchedQuotationDetails.delivery_fee ? (
                    <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                      <DetailBlock
                        blockTitle="Delivery Fee"
                        blockDesc={`${
                          selectedCountry.currency
                        } ${fetchedQuotationDetails.delivery_fee?.toFixed(2)}`}
                      />
                    </GridItem>
                  ) : (
                    ""
                  )}
                  {fetchedQuotationDetails.tax ? (
                    <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                      <DetailBlock
                        blockTitle="Tax"
                        blockDesc={`${
                          selectedCountry.currency
                        } ${fetchedQuotationDetails.tax?.toFixed(2)}`}
                      />
                    </GridItem>
                  ) : (
                    ""
                  )}
                  {fetchedQuotationDetails.grandtotal ? (
                    <GridItem xs={12} sm={12} md={4} lg={4} xl={4}>
                      <DetailBlock
                        blockTitle="Total"
                        blockDesc={`${
                          selectedCountry.currency
                        } ${fetchedQuotationDetails.grandtotal?.toFixed(2)}`}
                      />
                    </GridItem>
                  ) : (
                    ""
                  )}

                  <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                    <Divider />
                  </GridItem>

                  {fetchedQuotationDetails.order_delivery_details &&
                  fetchedQuotationDetails.order_delivery_details.length > 0 ? (
                    <GridItem xs={12} sm={12} md={6} lg={6} xl={6}>
                      <DetailBlock
                        blockTitle="Delivery Details"
                        blockDesc={
                          <>
                            {fetchedQuotationDetails.order_delivery_details[0].first_name +
                              " " +
                              fetchedQuotationDetails.order_delivery_details[0].last_name}
                            <br />
                            {fetchedQuotationDetails.order_delivery_details[0].address1}
                            <br />
                            {fetchedQuotationDetails.order_delivery_details[0].address2}
                            <br />
                            {fetchedQuotationDetails.order_delivery_details[0].states?.state_name}
                            <br />
                            {fetchedQuotationDetails.order_delivery_details[0].contact_phone}
                          </>
                        }
                      />
                    </GridItem>
                  ) : (
                    <GridItem xs={12} sm={12} md={6} lg={6} xl={6}>
                      <DetailBlock
                        blockTitle="Delivery Details"
                        blockDesc={fetchedQuotationDetails.billing_address}
                      />
                    </GridItem>
                  )}
                  {fetchedQuotationDetails.billing_address && (
                    <GridItem xs={12} sm={12} md={6} lg={6} xl={6}>
                      <DetailBlock
                        blockTitle="Billing Address"
                        blockDesc={fetchedQuotationDetails.billing_address}
                      />
                    </GridItem>
                  )}
                </GridContainer>
              </MDBox>
            </MDBox>
          )}
          <ConvertToOrderDialog
            open={openConvertDialog}
            onClose={() => setOpenConvertDialog(false)}
            onSubmit={handleConvertToOrder}
            initialValues={{}}
            loading={false}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "stretch" }}>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Box>
              <Tooltip title="Resend Quote Email" placement="top">
                <MDButton
                  variant="gradient"
                  size="small"
                  color="warning"
                  sx={{
                    mx: 1,
                    color: "#fff",
                    fontSize: "10px!important",
                    fontWeight: "400",
                  }}
                  onClick={() => {
                    Confirm.show(
                      `Please Confirm`,
                      "Are you sure you want resend the quote email?",
                      "Yes",
                      "No",
                      () => {
                        Loading.dots({
                          svgColor: "rgba(159, 3, 3, 0.8)",
                        });
                        resendQuoteCreatedEmail({ quoteId: fetchedQuotationDetails.id });
                        Notify.success("Successfully sent!");
                        Loading.remove(500);
                      },
                      () => {},
                      {}
                    );
                  }}
                >
                  Resend eMail
                </MDButton>
              </Tooltip>
              {fetchedQuotationDetails.zoho_salesorder_id && (
                <MDButton
                  type="submit"
                  color="primary"
                  variant="gradient"
                  target="_blank"
                  href={
                    "https://inventory.zoho.com/app/868243051#/salesorders/" +
                    fetchedQuotationDetails.zoho_salesorder_id
                  }
                  sx={{ mr: 1 }}
                >
                  View Sales Order
                </MDButton>
              )}
              <MDButton color="secondary" variant="gradient" autoFocus onClick={handleClose}>
                Close
              </MDButton>
            </Box>
          </Box>
        </DialogActions>
      </BootstrapDialog>
    </>
  );
};

OrderDetails.propTypes = {
  order: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
};

export default OrderDetails;
